import React, { useState } from 'react';
import { Gift, Smartphone, Laptop, Wifi, Zap, Shield, TrendingUp, AlertCircle, CheckCircle, Star, Clock, Users } from 'lucide-react';

const ElectronicsPage = ({ setCurrentPage }) => {
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedBudget, setSelectedBudget] = useState('all');

  const techLevels = [
    { id: 'beginner', name: 'Tech-nybegynner', icon: Users, color: 'green' },
    { id: 'everyday', name: 'Hverdagsbruker', icon: Smartphone, color: 'blue' },
    { id: 'enthusiast', name: 'Tech-entusiast', icon: Laptop, color: 'purple' },
    { id: 'professional', name: 'Profesjonell', icon: Shield, color: 'indigo' }
  ];

  const budgetRanges = [
    { id: 'budget', name: 'Under 1000 kr', min: 0, max: 1000, color: 'green' },
    { id: 'mid', name: '1000-3000 kr', min: 1000, max: 3000, color: 'blue' },
    { id: 'premium', name: '3000-8000 kr', min: 3000, max: 8000, color: 'purple' },
    { id: 'luxury', name: 'Over 8000 kr', min: 8000, max: 999999, color: 'red' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-50 via-accent-50 to-purple-50 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-4xl animate-pulse">⚡</div>
          <div className="absolute top-20 right-20 text-3xl animate-bounce">📱</div>
          <div className="absolute bottom-20 left-20 text-5xl animate-pulse">💻</div>
          <div className="absolute bottom-10 right-10 text-4xl animate-bounce">🎧</div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">📱</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Elektronikk-gaver</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Gi teknologi som forbedrer hverdagen og åpner nye muligheter. Fra praktiske hverdagshjelpere 
              til cutting-edge innovasjon - finn elektronikk som virkelig gjør en forskjell.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn tech-gaver
            </button>
          </div>
        </div>
      </section>

      {/* Tech Philosophy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor er elektronikk fantastiske gaver?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <TrendingUp className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Forbedrer hverdagen</h3>
                <p className="text-secondary-600">
                  God teknologi gjør livet enklere, mer effektivt og ofte mer morsomt. Det er gaver som 
                  brukes daglig og som mottakeren blir glad for hver gang de bruker dem.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Zap className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Åpner nye muligheter</h3>
                <p className="text-secondary-600">
                  Teknologi kan gi tilgang til nye hobbyer, læringsmuligheter og måter å koble seg til verden på. 
                  Det er gaver som kan inspirere og utvide horisonter.
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Shield className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Varig verdi</h3>
                <p className="text-secondary-600">
                  Kvalitets-elektronikk holder i årevis og kan ofte oppdateres. Det er en investering 
                  som fortsetter å gi verdi lenge etter gaveanledningen.
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Wifi className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">Teknologi som kobler oss sammen</h3>
                  <p className="text-warning-700">
                    I vår digitale verden er teknologi ikke bare verktøy - det er måten vi holder kontakt med 
                    familie, utforsker interesser, jobber kreativt og kobler oss til verden rundt oss. 
                    En god elektronikk-gave kan bokstavelig talt åpne nye verdener.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Level Guide */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Velg teknologi basert på deres tech-nivå
            </h2>

            {/* Level Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedLevel('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedLevel === 'all'
                    ? 'bg-indigo-500 text-white border-indigo-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-indigo-300'
                }`}
              >
                Alle nivåer
              </button>
              {techLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setSelectedLevel(level.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedLevel === level.id
                      ? `bg-${level.color}-500 text-white border-${level.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <level.icon className="w-4 h-4" />
                  <span>{level.name}</span>
                </button>
              ))}
            </div>

            {/* Level Cards */}
            <div className="space-y-8">
              {/* Beginner */}
              <div className={`${selectedLevel === 'all' || selectedLevel === 'beginner' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Users className="w-10 h-10 text-success-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Tech-nybegynner</h3>
                      <p className="text-secondary-600">For de som vil ha enkle, intuitive løsninger</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📱 Kommunikasjon gjort enkelt</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Enkle smartphones med store skjermer</li>
                        <li>• Tablets for videochat med familie</li>
                        <li>• Seniorvennlige telefoner</li>
                        <li>• Bilderamme som oppdateres fra mobilappen</li>
                      </ul>
                      <div className="mt-3 text-xs text-success-700 bg-success-50 p-2 rounded">
                        💡 Fokus på store knapper, tydelige ikoner og god kundeservice
                      </div>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏠 Smart hjem - enkelt</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Smart høyttalere med stemmeassistent</li>
                        <li>• Enkle smart-pærer</li>
                        <li>• Automatiske støvsugere</li>
                        <li>• Smart termostat med app</li>
                      </ul>
                      <div className="mt-3 text-xs text-success-700 bg-success-50 p-2 rounded">
                        💡 Produkter som "bare fungerer" uten komplisert oppsett
                      </div>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💝 Hverdagshjelpere</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• E-book lesere med store skrifter</li>
                        <li>• DAB-radioer med presets</li>
                        <li>• Enkle fitness-trackere</li>
                        <li>• Automatiske medisindispensere</li>
                      </ul>
                      <div className="mt-3 text-xs text-success-700 bg-success-50 p-2 rounded">
                        💡 Teknologi som løser hverdagsproblemer på en enkel måte
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Everyday User */}
              <div className={`${selectedLevel === 'all' || selectedLevel === 'everyday' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Smartphone className="w-10 h-10 text-accent-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Hverdagsbruker</h3>
                      <p className="text-secondary-600">Komfortabel med grunnleggende tech, ønsker oppgraderinger</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎧 Audio og underholdning</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• AirPods eller tilsvarende trådløse ørepropper</li>
                        <li>• Bluetooth høyttalere for hjemmet</li>
                        <li>• Streaming-enheter (Apple TV, Chromecast)</li>
                        <li>• Gode over-ear hodetelefoner</li>
                      </ul>
                    </div>
                    
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💻 Produktivitet og arbeid</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Trådløse tastaturer og mus</li>
                        <li>• Portable skjermer for laptop</li>
                        <li>• Webcam med god kvalitet</li>
                        <li>• Ergonomiske tilbehør</li>
                      </ul>
                    </div>
                    
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">⌚ Wearables og helse</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Apple Watch eller Garmin fitness-klokker</li>
                        <li>• Smart vekt med app-integrasjon</li>
                        <li>• Søvnsporing-enheter</li>
                        <li>• Pulsmålere for trening</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-accent-50 border border-accent-200 rounded-lg p-4">
                    <h4 className="font-semibold text-accent-800 mb-2">💡 Perfekt for hverdagsbrukere:</h4>
                    <p className="text-accent-700 text-sm">
                      Disse brukerne verdsetter teknologi som "bare fungerer" og gjør hverdagen bedre. 
                      De vil gjerne ha oppgraderinger av ting de allerede bruker, men uten for mye kompleksitet.
                    </p>
                  </div>
                </div>
              </div>

              {/* Enthusiast */}
              <div className={`${selectedLevel === 'all' || selectedLevel === 'enthusiast' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Laptop className="w-10 h-10 text-purple-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Tech-entusiast</h3>
                      <p className="text-secondary-600">Elsker ny teknologi og avanserte funksjoner</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎮 Gaming og underholdning</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Mekaniske gaming-tastaturer</li>
                        <li>• High-end gaming-mus med RGB</li>
                        <li>• VR-headset (Meta Quest, PlayStation VR)</li>
                        <li>• 4K gaming-skjermer med høy refresh rate</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📸 Kreativt innhold</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Mirrorless kameraer</li>
                        <li>• Drone med 4K kamera</li>
                        <li>• Podcast/streaming utstyr</li>
                        <li>• Grafiske tablets for digital kunst</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🔧 DIY og utvikling</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Raspberry Pi og Arduino kits</li>
                        <li>• 3D-printere</li>
                        <li>• Elektrisk verktøy med smart-funksjoner</li>
                        <li>• Hjemme-lab utstyr</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">🚀 For de som elsker det nyeste:</h4>
                    <p className="text-purple-700 text-sm">
                      Tech-entusiaster vil gjerne ha den nyeste teknologien, eksperimentere med nye produkter 
                      og pushe grensene for hva som er mulig. De setter pris på avanserte funksjoner og customization.
                    </p>
                  </div>
                </div>
              </div>

              {/* Professional */}
              <div className={`${selectedLevel === 'all' || selectedLevel === 'professional' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Shield className="w-10 h-10 text-indigo-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Profesjonell bruker</h3>
                      <p className="text-secondary-600">Trenger pålitelig, høyytelsesverktøy for arbeid</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💼 Profesjonell produktivitet</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• High-end bærbare datamaskiner</li>
                        <li>• Profesjonelle skjermer (4K/5K)</li>
                        <li>• Ergonomiske kontorløsninger</li>
                        <li>• Enterprise-grade sikkerhetsutstyr</li>
                      </ul>
                    </div>
                    
                    <div className="border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎨 Kreativ profesjonell</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Profesjonelle kameraer (Canon, Sony, Nikon)</li>
                        <li>• Video-redigeringsutstyr</li>
                        <li>• Audio-opptak for studio-kvalitet</li>
                        <li>• Grafiske workstations</li>
                      </ul>
                    </div>
                    
                    <div className="border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🔒 Sikkerhet og infrastruktur</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Enterprise WiFi-løsninger</li>
                        <li>• Backup og cloud-storage systemer</li>
                        <li>• Cybersikkerhet verktøy</li>
                        <li>• Server og nettverksutstyr</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-800 mb-2">⚡ Profesjonell standard:</h4>
                    <p className="text-indigo-700 text-sm">
                      Profesjonelle brukere trenger utstyr som kan håndtere tunge arbeidsbelastninger, 
                      tilbyr pålitelighet og har god support. Investeringen må kunne forsvares gjennom produktivitetsgevinst.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Shopping Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Smart shopping for elektronikk-gaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <CheckCircle className="w-8 h-8 text-success-500 mr-3" />
                    <h3 className="text-xl font-semibold text-success-800">Smart kjøpsstrategi</h3>
                  </div>
                  <ul className="space-y-2 text-success-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-success-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Research mottakerens eksisterende tech-økosystem (iOS vs Android, etc.)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-success-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Les anmeldelser fra multiple kilder før du kjøper</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-success-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Sjekk kompatibilitet med deres eksisterende utstyr</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-success-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Vurder fremtidig oppgraderbarhet og support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-success-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Kjøp fra butikker med god returpolicy</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Clock className="w-8 h-8 text-accent-500 mr-3" />
                    <h3 className="text-xl font-semibold text-accent-800">Timing og tilbud</h3>
                  </div>
                  <ul className="space-y-2 text-accent-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Black Friday og Cyber Monday for beste priser</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Januar-salg for forrige års modeller</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Studentrabatter hvis mottakeren er student</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Kjøp rett etter nye produktlanseringer for bedre priser på eldre modeller</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <AlertCircle className="w-8 h-8 text-error-500 mr-3" />
                    <h3 className="text-xl font-semibold text-error-800">Vanlige feil å unngå</h3>
                  </div>
                  <ul className="space-y-2 text-error-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Ikke anta at nyeste = beste for alle brukere</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Ikke glem nødvendige tilbehør (kabler, ladere, covers)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Ikke kjøp teknologi du ikke forstår selv</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Ikke stol kun på merke - sjekk spesifikasjoner</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Ikke kjøp refurbished produkter uten god garanti</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Star className="w-8 h-8 text-warning-500 mr-3" />
                    <h3 className="text-xl font-semibold text-warning-800">Pro tips</h3>
                  </div>
                  <ul className="space-y-2 text-warning-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Pakk inn med kvittering synlig - tech kan trenge byttes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Tilby å hjelpe med oppsett hvis produktet er komplisert</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Vurder gavekort til tech-butikker hvis du er usikker</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>Legg ved instruksjoner på norsk hvis produktet kun har engelsk manual</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Elektronikk for ethvert budsjett
            </h2>

            {/* Budget Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedBudget('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedBudget === 'all'
                    ? 'bg-indigo-500 text-white border-indigo-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-indigo-300'
                }`}
              >
                Alle budsjetter
              </button>
              {budgetRanges.map((budget) => (
                <button
                  key={budget.id}
                  onClick={() => setSelectedBudget(budget.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    selectedBudget === budget.id
                      ? `bg-${budget.color}-500 text-white border-${budget.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  {budget.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {budgetRanges.map((budget, index) => (
                <div
                  key={budget.id}
                  className={`bg-white rounded-lg shadow-sm p-6 border border-secondary-100 ${
                    selectedBudget === 'all' || selectedBudget === budget.id ? 'block' : 'hidden'
                  }`}
                >
                  <div className={`text-center mb-4`}>
                    <div className={`text-3xl font-bold text-${budget.color}-600 mb-2`}>
                      {budget.name}
                    </div>
                    <div className="text-sm text-secondary-500">
                      {budget.id === 'budget' && 'Starter-tech og tilbehør'}
                      {budget.id === 'mid' && 'Solid mainstream-produkter'}
                      {budget.id === 'premium' && 'High-end og flaggskip'}
                      {budget.id === 'luxury' && 'Profesjonell og luksus-tech'}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {budget.id === 'budget' && (
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">Populære valg:</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Bluetooth høyttaler (300-800 kr)</li>
                          <li>• Powerbank og ladekabler (200-600 kr)</li>
                          <li>• Enkle fitness-trackere (400-900 kr)</li>
                          <li>• DAB-radio eller smartklokke (500-1000 kr)</li>
                        </ul>
                      </div>
                    )}

                    {budget.id === 'mid' && (
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">Populære valg:</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Trådløse ørepropper (1000-2500 kr)</li>
                          <li>• Tablet eller e-reader (1500-3000 kr)</li>
                          <li>• Smart home starter-kit (1200-2800 kr)</li>
                          <li>• Gaming-tilbehør (1000-3000 kr)</li>
                        </ul>
                      </div>
                    )}

                    {budget.id === 'premium' && (
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">Populære valg:</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Apple Watch eller premium smartklokke</li>
                          <li>• iPad Air eller Samsung Galaxy Tab</li>
                          <li>• High-end hodetelefoner (Sony, Bose)</li>
                          <li>• Gaming-konsoll eller VR-headset</li>
                        </ul>
                      </div>
                    )}

                    {budget.id === 'luxury' && (
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">Populære valg:</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• iPhone Pro eller Samsung Galaxy Ultra</li>
                          <li>• MacBook eller premium Windows laptop</li>
                          <li>• Profesjonelle kameraer</li>
                          <li>• High-end TV eller projektor</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Future-Proofing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Fremtidssikre elektronikk-gaver
            </h2>

            <div className="space-y-8">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <TrendingUp className="w-8 h-8 text-indigo-500 mr-3" />
                  <h3 className="text-xl font-semibold text-indigo-800">Teknologi-trender å følge med på</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-800 mb-3">🚀 Kommende teknologier:</h4>
                    <ul className="text-sm text-secondary-700 space-y-1">
                      <li>• AI-integrerte enheter blir standard</li>
                      <li>• 5G og forbedret tilkobling</li>
                      <li>• Bærekraftig og modulær design</li>
                      <li>• Forbedret batterikapasitet og trådløs lading</li>
                      <li>• AR/VR blir mer mainstream</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-800 mb-3">💡 Invester i:</h4>
                    <ul className="text-sm text-secondary-700 space-y-1">
                      <li>• Produkter med regelmessige software-oppdateringer</li>
                      <li>• Økosystem-kompatible enheter</li>
                      <li>• Modulære og oppgraderbare produkter</li>
                      <li>• Enheter med lang batterilevetid</li>
                      <li>• Merker med god supporthistorikk</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Shield className="w-8 h-8 text-success-500 mr-3" />
                  <h3 className="text-xl font-semibold text-success-800">Bærekraftige valg</h3>
                </div>
                <p className="text-success-700 mb-4">
                  Elektronikk har stor miljøpåvirkning, så velg produkter som varer lenge og kan repareres eller oppgraderes.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-semibold text-secondary-800 mb-2">♻️ Bærekraftige merker:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Fairphone (modulære telefoner)</li>
                      <li>• Framework (oppgraderbare laptops)</li>
                      <li>• Refurbished Apple produkter</li>
                      <li>• Produkter med lang garanti</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-800 mb-2">🔄 Sirkulær økonomi:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kjøp refurbished når mulig</li>
                      <li>• Velg reparerbare produkter</li>
                      <li>• Lever inn gammelt utstyr til resirkulering</li>
                      <li>• Vurder leasing/subscription-modeller</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-800 mb-2">⚡ Energieffektivitet:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Sjekk energimerkingen</li>
                      <li>• Velg enheter med god batterikapasitet</li>
                      <li>• Solar-ladere for utendørsbruk</li>
                      <li>• Smart home for energisparing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Zap className="w-16 h-16 text-indigo-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-secondary-800 mb-6">
              Ikke sikker på hvor du skal starte?
            </h2>
            <div className="text-left bg-white rounded-lg shadow-sm p-8 border border-indigo-200">
              <h3 className="text-xl font-semibold text-secondary-800 mb-4">Her er en enkel framgangsmåte:</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">Kartlegg deres tech-økosystem</h4>
                    <p className="text-secondary-600 text-sm">iPhone eller Android? Mac eller PC? Dette påvirker kompatibilitet.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">Vurder deres tech-nivå</h4>
                    <p className="text-secondary-600 text-sm">Er de komfortable med ny teknologi eller foretrekker de enkle løsninger?</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">Identifiser problemer de kan ha</h4>
                    <p className="text-secondary-600 text-sm">Klager de over dårlig batteritid? Treg datamaskin? Dårlig lyd? Start der.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">Research grundig</h4>
                    <p className="text-secondary-600 text-sm">Les anmeldelser, sammenlign alternativer og sjekk kompatibilitet før du kjøper.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte tech-gaven?
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Bruk vår gavefinner for å få personlige anbefalinger basert på mottakerens tech-nivå og ditt budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-indigo-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('category')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-indigo-600 transition-colors font-semibold"
              >
                Se andre kategorier
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ElectronicsPage;